import { Helmet } from '@dr.pogodin/react-helmet';
import { COMPANY_NAME, DEFAULT_OG_IMAGE, SITE_URL } from '../../content/site';
import { PageSEO } from '../../content/types';

export interface SeoProps {
  title: string;
  description: string;
  /** Path ("/services") or an absolute URL. Resolved against SITE_URL. */
  canonical: string;
  /** Absolute URL or public-root path. Falls back to the site-wide card. */
  ogImage?: string;
  /** Admin, auth errors, and 404 -- anything Google must not index. */
  noIndex?: boolean;
  ogType?: 'website' | 'article';
}

/**
 * Builds an absolute URL against the canonical origin.
 *
 * The trailing slash is stripped everywhere except the root: Google treats
 * `/services` and `/services/` as two URLs, and the sitemap, the canonical
 * tag, and og:url all have to name the same one.
 */
export const absoluteUrl = (pathOrUrl: string): string => {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path.replace(/\/+$/, '')}`;
};

/**
 * Spreads a PAGE_SEO entry onto <Seo>, using its `path` as the canonical.
 * Keeps the per-page call site to one line without dropping type checking on
 * the props that aren't part of PageSEO.
 */
export const seoPropsFor = (page: PageSEO): SeoProps => ({
  title: page.title,
  description: page.description,
  canonical: page.path,
  noIndex: page.noIndex,
});

/**
 * Per-page document head. Every route renders exactly one of these.
 *
 * public/index.html carries a title and description as a no-JS fallback only;
 * it deliberately carries no canonical and no og:url, because a static global
 * canonical pointed every inner page at the homepage.
 */
const Seo = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
  ogType = 'website',
}: SeoProps) => {
  const url = absoluteUrl(canonical);
  const image = absoluteUrl(ogImage);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {/* No canonical on noindex routes. /admin/* shares one <Seo>, so a
          canonical here would point every admin child at /admin -- and a
          canonical on a page Google is told to drop is noise either way. */}
      {!noIndex && <link rel="canonical" href={url} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:site_name" content={COMPANY_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

export default Seo;
